import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormGroup, Label, Input, Button, Table, Col, Row, Card, CardBody, CardHeader } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { invoiceActions } from '../../sagas/invoiceSlice';
import { companyActions } from '../../sagas/companySlice';
import Swal from 'sweetalert2';
import { formatPrice } from '../../helpers/utility';
import { format } from 'date-fns';
import { FaMoneyBillWave } from 'react-icons/fa';

const InvoiceForm = ({ initialValues, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [invoice, setInvoice] = useState(initialValues || {});
    const { isLoading, invoiceNumber } = useSelector(state => state.invoices);
    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unite
    const { companies } = useSelector(state => state.companies);

    useEffect(() => {
        if (!initialValues?.numero) {
            setInvoice(prevInvoice => ({ ...prevInvoice, numero: invoiceNumber }));
        }

        dispatch(invoiceActions.fetchNewInvoiceNumber({companyId: invoice?.companyId}));

        if (!companies || companies.length === 0) {
            dispatch(companyActions.getAll({}));
        }

    }, [initialValues, invoiceNumber, companies, dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInvoice({ ...invoice, [name]: value });
    };

    const selectedCompanyName = invoice.companyName
        || companies?.find((company) => Number(company.id) === Number(invoice.companyId))?.name
        || '';


    const submitInvoice = () => {

        const invoiceData = {
            ...invoice,
            companyId: invoice.companyId ? Number(invoice.companyId) : null,
            bonLivraionsRefs: invoice.deliveryMappings.map(delivery => delivery.numero).join(','),
            dateFacture: invoice.dateFacture || format(new Date(), 'yyyy-MM-dd'),
            dateEcheance: invoice.dateEcheance,
            modeReglement: invoice.modeReglement,
            statut: 'IMPAYE',
        };

        let payload = {
            invoice: invoiceData,
            onSuccess: (savedInvoice) => {
                Swal.fire({
                    icon: 'success',
                    title: t('facture.invoiceSaved'),
                    text: t('facture.invoiceSavedMessage', { invoiceNumber: savedInvoice.numero }),
                });
                onClose();
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('facture.invoiceSaveError'),
                    text: t('facture.invoiceSaveErrorMessage', { error }),
                });
            },
        };

        if (invoice.id) {
            dispatch(invoiceActions.updateInvoice(payload));
        } else {
            dispatch(invoiceActions.addInvoice(payload));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!invoice.dateEcheance) {
            Swal.fire({
                icon: 'warning',
                title: t('facture.validation.dueDateRequired'),
                text: t('facture.validation.dueDateRequiredMessage'),
            });
            return;
        }

        if (!invoice.companyId) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('facture.validation.companyRequiredMessage', 'Veuillez sélectionner une entreprise avant de créer la facture.'),
            });
            return;
        }

        if (!invoice.modeReglement) {
            Swal.fire({
                icon: 'warning',
                title: t('facture.validation.paymentMethodRequired'),
                text: t('facture.validation.paymentMethodRequiredMessage'),
            });
            return;
        }

        // Show confirmation dialog
        Swal.fire({
            title: t('facture.confirmSubmit'),
            text: t('facture.confirmSubmitMessage'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                submitInvoice();
            }
        });
    };

    // Calculate totals
    const totalHT = invoice.montantTotalHT || 0;
    const totalTVA = invoice.montantTotalTaxe || 0;
    const totalTTC = invoice.montantTotalTTC || 0;
    const totalAmountPaid = invoice.montantRegle || 0;

    return (
        <Form onSubmit={handleSubmit}>
            <Card>
                <CardBody>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.invoiceNumber')}</Label>
                                <Input
                                    type="text"
                                    value={invoice.numero || ''}
                                    disabled
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('company.company', 'Entreprise')}</Label>
                                <Input
                                    type="text"
                                    value={selectedCompanyName}
                                    disabled
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.client')}</Label>
                                <Input
                                    type="text"
                                    value={invoice.clientNom || ''}
                                    disabled
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.invoiceDate')}</Label>
                                <Input
                                    type="date"
                                    name="dateFacture"
                                    value={invoice.dateFacture || format(new Date(), 'yyyy-MM-dd')}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.dueDate')}</Label>
                                <Input
                                    type="date"
                                    name="dateEcheance"
                                    value={invoice.dateEcheance || ''}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Card className="mb-3">
                        <CardHeader>
                            <h5 className="mb-0">{t('delivery.deliveryNotes')}</h5>
                        </CardHeader>
                        <CardBody>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('delivery.numero')}</th>
                                        <th>{t('delivery.deliveryDate')}</th>
                                        <th>{t('facture.totalAmountTTC')}</th>
                                        <th>{t('sale.amountPaid')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.deliveryMappings && invoice.deliveryMappings.map((delivery, index) => (
                                        <tr key={index}>
                                            <td>{delivery.numero}</td>
                                            <td>{delivery.dateLivraison}</td>
                                            <td>{formatPrice(delivery.montantTTC, false)}</td>
                                            <td>{formatPrice(delivery.montantPaye, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>

                    <Card className="mb-3">
                        <CardHeader>
                            <h5 className="mb-0">{t('facture.invoiceLines')}</h5>
                        </CardHeader>
                        <CardBody>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('facture.productCode')}</th>
                                        <th>{t('facture.productDescription')}</th>
                                        <th>{t('facture.quantity')}</th>
                                        <th>{t('facture.unit')}</th>
                                        <th>{t('facture.unitPriceHT')}</th>
                                        <th>{t('facture.prixUnitaireTTC')}</th>
                                        <th>{t('facture.totalHt')}</th>
                                        <th>{t('facture.tva')}</th>
                                        <th>{t('facture.totalTtc')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.lignes && invoice.lignes.map((line, index) => (
                                        <tr key={index}>
                                            <td>{line.articleCode}</td>
                                            <td>{line.articleNom}</td>
                                            <td>{line.quantite}</td>
                                            <td>{unites.find(u => u.code === line.unite)?.libelle}</td>
                                            <td>{formatPrice(line.prixUnitaireHT, false)}</td>
                                            <td>{formatPrice(line.prixUnitaireTTC, false)}</td>
                                            <td>{formatPrice(line.montantHT, false)}</td>
                                            <td>{(line.tauxTVA * 100).toFixed(0)} %</td>
                                            <td>{formatPrice(line.montantTTC, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('facture.comment')}</Label>
                                <Input
                                    type="textarea"
                                    name="commentaire"
                                    value={invoice.commentaire || ''}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="modeReglement">
                                    <FaMoneyBillWave className="me-1" />
                                    {t('facture.modeReglement')} *
                                </Label>
                                <Input
                                    type="select"
                                    id="modeReglement"
                                    name="modeReglement"
                                    value={invoice.modeReglement}
                                    onChange={(e) => setInvoice({ ...invoice, modeReglement: e.target.value })}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                    <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                    <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-3 justify-content-end">
                        <Col md={4}>
                            <div className="totals p-3 border rounded bg-light">
                                <p className="d-flex justify-content-between">
                                    <strong>{t('facture.totalHt')}:</strong>
                                    <span>{formatPrice(totalHT)}</span>
                                </p>
                                <p className="d-flex justify-content-between">
                                    <strong>{t('facture.tva')}:</strong>
                                    <span>{formatPrice(totalTVA)}</span>
                                </p>
                                <hr />
                                <p className="d-flex justify-content-between fs-5">
                                    <strong>{t('facture.totalTtc')}:</strong>
                                    <span>{formatPrice(totalTTC)}</span>
                                </p>
                                <p className="d-flex justify-content-between">
                                    <strong>{t('sale.amountPaid')}:</strong>
                                    <span>{formatPrice(totalAmountPaid)}</span>
                                </p>
                            </div>
                        </Col>
                    </Row>

                    <div className="mt-4 d-flex justify-content-end">
                        <Button color="secondary" onClick={onClose} className="me-2">
                            {t('common.cancel')}
                        </Button>
                        <Button color="primary" type="submit" disabled={isLoading}>
                            {isLoading ? t('common.saving') : t('common.save')}
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </Form>
    );
};

export default InvoiceForm;
